import {
  Text,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Icon,
  VStack,
  Grid,
  StackProps,
  Collapse,
  Box,
  Center,
  Flex,
  Tooltip,
  Link,
  BoxProps,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useState } from "react";
import {
  FaCheck,
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  isFinished,
  isLoading,
  isSuccess,
} from "../../../../../common/helper/commonHelper";
import { ImportReq, Matches } from "../../../../../common/types/person";
import IButton from "../../../../../components/IButton";
import IModal, { ModalButtonContent } from "../../../../../components/IModal";
import { CsvUpload } from "../../personDbSlice";

type AdditionalOptionsType = Omit<ImportReq, "filepath" | "enable_update">;

enum UPLOAD_STATUS {
  "ERROR" = 1,
  "DUPLICATE",
  "SUCCESS",
}

function statusFinder(data: Matches) {
  if (data.error_contacts) {
    return UPLOAD_STATUS.ERROR;
  } else if (data.duplicate_contacts) {
    return UPLOAD_STATUS.DUPLICATE;
  } else if (data.total_contacts) {
    return UPLOAD_STATUS.SUCCESS;
  } else {
    return UPLOAD_STATUS.ERROR;
  }
}

function ModalHeader({ status }: { status: UPLOAD_STATUS }) {
  const headers = {
    [UPLOAD_STATUS.ERROR]: "Data contains errors",
    [UPLOAD_STATUS.DUPLICATE]: "Data contains duplicates",
    [UPLOAD_STATUS.SUCCESS]: "Data verified successfully",
  };
  const icons = {
    [UPLOAD_STATUS.ERROR]: (
      <Icon fontSize="lg" as={FaExclamationCircle} color="red.500"></Icon>
    ),
    [UPLOAD_STATUS.DUPLICATE]: (
      <Icon fontSize="lg" as={FaExclamationTriangle} color="orange.500"></Icon>
    ),
    [UPLOAD_STATUS.SUCCESS]: (
      <Icon fontSize="lg" as={FaCheck} color="green.500"></Icon>
    ),
  };

  return (
    <HStack>
      {icons[status] || icons[UPLOAD_STATUS.ERROR]}
      <Heading fontSize="sm">
        {headers[status] || headers[UPLOAD_STATUS.ERROR]}
      </Heading>
    </HStack>
  );
}

function TextWithTooltip({
  text,
  tooltip,
}: {
  text: string;
  tooltip?: string | ReactNode;
}) {
  return (
    <HStack>
      <Text fontSize="sm">{text}</Text>
      {tooltip && (
        <Tooltip label={tooltip}>
          <Box>
            <Icon fontSize="xs" as={FaExclamationCircle} />
          </Box>
        </Tooltip>
      )}
    </HStack>
  );
}

function StatsRow({
  label,
  data,
  tooltip,
}: {
  label: string;
  data: number;
  tooltip?: string;
}) {
  return (
    <Grid fontSize="sm" templateColumns="1fr 1fr" w="100%">
      <TextWithTooltip text={label} tooltip={tooltip} />
      <Text>{data}</Text>
    </Grid>
  );
}

function Stats({
  data,
  showUpdateCount,
}: {
  data: Matches;
  showUpdateCount: boolean;
}) {
  return (
    <VStack alignItems="flex-start" width="100%" my="2">
      <StatsRow label="Total entries" data={data.total_contacts} />
      <StatsRow label="New entries" data={data.new_contacts} />
      <StatsRow label="Errors" tooltip="" data={data.error_contacts} />
      <Divider />
      <StatsRow
        label="Total entries to be created"
        data={data.total_entries_to_be_created}
      />
      {showUpdateCount && (
        <StatsRow
          label="Total entries to be updated"
          data={data.total_entries_to_be_updated}
        />
      )}
    </VStack>
  );
}

function AdditionalOptions({
  data,
  onChange,
}: {
  data: AdditionalOptionsType;
  onChange: (data: AdditionalOptionsType) => void;
}) {
  const [overwriteValues, setOverwriteValues] = useState(data.overwrite_values);
  const [ignoreBlanks, setIgnoreBlankValues] = useState(data.ignore_blank);

  useEffect(() => {
    setOverwriteValues(data.overwrite_values);
    setIgnoreBlankValues(data.ignore_blank);
  }, [data]);

  function onChangeHandler(field: keyof typeof data, val: boolean) {
    if (field === "overwrite_values") {
      setOverwriteValues(val);
      onChange({ ignore_blank: data.ignore_blank, overwrite_values: val });
    } else if (field === "ignore_blank") {
      setIgnoreBlankValues(val);
      onChange({
        ignore_blank: val,
        overwrite_values: data.overwrite_values,
      });
    }
  }

  return (
    <Box w="100%">
      <Checkbox
        checked={overwriteValues}
        onChange={(e) => onChangeHandler("overwrite_values", e.target.checked)}
        defaultChecked={false}
        fontSize="sm"
        name="overwrite-values"
        width="300px"
      >
        <TextWithTooltip
          text="Overwrite with new values"
          tooltip={
            <>
              <Text>
                If enabled, the update function would overwrite the old values
                in the existing contact record.
              </Text>
              <Text>
                If disabled, the system would only update a field value if its
                already empty.
              </Text>
            </>
          }
        />
      </Checkbox>

      <Checkbox
        checked={!ignoreBlanks}
        onChange={(e) => onChangeHandler("ignore_blank", !e.target.checked)}
        defaultChecked={false}
        fontSize="sm"
        name="ignore-blank-values"
        width="300px"
      >
        <TextWithTooltip
          text="Overwrite with blank values"
          tooltip={
            <>
              <Text>
                If enabled, the system would update a field value as blank in
                Inflection contact if the column in the csv has an empty value.
              </Text>
              <Text>
                If disabled, the no action will be taken if a column in the csv
                has empty value.
              </Text>
            </>
          }
        />
      </Checkbox>
    </Box>
  );
}

function BoxBorder({ children, ...props }: { children: ReactNode } & BoxProps) {
  return (
    <Box rounded="md" border="1px" borderColor="gray.100" w="100%" {...props}>
      {children}
    </Box>
  );
}

function BoxContainer({
  children,
  ...props
}: { children: ReactNode } & StackProps) {
  return (
    <Flex
      flexDir="column"
      alignItems="flex-start"
      width="100%"
      bg="white"
      p="5"
      {...props}
    >
      {children}
    </Flex>
  );
}

function EnableDataUpdate({
  value,
  onChange,
}: {
  value: boolean;
  onChange: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <Checkbox
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      defaultChecked={value}
      fontSize="sm"
      name="enable-data-update"
      my={2}
      width="300px"
    >
      <TextWithTooltip
        text="Update existing records"
        tooltip="Enable this option to update the existing contact record with values for matched emails from the uploaded document"
      />
    </Checkbox>
  );
}

function UploadStats({
  success,
  updated,
  created,
  uploadAgain,
}: {
  success: boolean;
  updated: number;
  created: number;
  uploadAgain: () => void;
}) {
  function TextRow({ count, text }: { count: number; text: string }) {
    return (
      <Text fontSize="md" fontWeight="medium">
        <Text as="span" fontWeight="bold">
          {count}
        </Text>{" "}
        {text}
      </Text>
    );
  }

  return (
    <Center h="200px" bg="white">
      <Flex alignItems="center" flexDir="column">
        {success ? (
          <>
            <Icon
              color="green.300"
              mb="4"
              h="12"
              w="12"
              as={FaCheckCircle}
            ></Icon>
            <Flex flexDir="column" mb="10">
              <TextRow count={created} text="contacts successfully created" />
              <TextRow count={updated} text="contacts successfully updated" />
            </Flex>
          </>
        ) : (
          <Icon
            color="red.300"
            mb="4"
            h="12"
            w="12"
            as={FaExclamationCircle}
          ></Icon>
        )}

        <IButton
          size="xs"
          onClick={uploadAgain}
          variant="link"
          capitalize={false}
        >
          {success
            ? "Click here to upload another set of contacts"
            : "Upload failed. Click here to try again"}
        </IButton>
      </Flex>
    </Center>
  );
}

function UploadStatusNote({ status }: { status: UPLOAD_STATUS }) {
  const notes = {
    [UPLOAD_STATUS.ERROR]: (
      <Text mt="3" fontSize="sm" color="red.400">
        Note: The System cannot import the contacts from the document until all
        the errors are resolved.
      </Text>
    ),
    [UPLOAD_STATUS.DUPLICATE]: (
      <Text mt="3" fontSize="sm" color="orange.500">
        Note: The System will ignore the duplicate email entries and would
        import all the unique emails
      </Text>
    ),
    [UPLOAD_STATUS.SUCCESS]: <></>,
  };

  return notes[status];
}

function DownloadReport({
  status,
  link,
}: {
  status: UPLOAD_STATUS;
  link: string;
}) {
  const notes = {
    [UPLOAD_STATUS.DUPLICATE]: {
      title: "Download duplicates report",
      text: "The system will generate a report of duplicate data. You can remove the duplicates and then re-upload the file",
    },
    [UPLOAD_STATUS.ERROR]: {
      title: "Download error report",
      text: "The system will generate an error report with your data. You can correct the error and then re-upload the same file",
    },
    [UPLOAD_STATUS.SUCCESS]: {
      title: "",
      text: "",
    },
  };

  return (
    <>
      {status !== UPLOAD_STATUS.SUCCESS && (
        <Box bg="gray.100" px="5" py="3">
          <Text fontWeight="semibold" mb="2" fontSize="sm">
            {notes[status].title}
          </Text>
          <Grid templateColumns="2fr 1fr">
            <Text fontSize="sm">{notes[status].text}</Text>
            <Center>
              <Link href={link} target="_blank">
                <IButton size="sm" variant="outline" bg="white">
                  Download Report
                </IButton>
              </Link>
            </Center>
          </Grid>
        </Box>
      )}
    </>
  );
}

const ADD_OPTONS_INIT = {
  overwrite_values: false,
  ignore_blank: true,
};

function isDisabled(data: CsvUpload, enableDataUpdate: boolean): boolean {
  if (data.matches) {
    const isError = statusFinder(data.matches) === UPLOAD_STATUS.ERROR;
    const areEntriesEmpty =
      !data.matches.total_entries_to_be_created &&
      !data.matches.total_entries_to_be_updated;

    const areUpdatesEmpty =
      !data.matches.total_entries_to_be_created && !enableDataUpdate;

    return isError || areEntriesEmpty || areUpdatesEmpty;
  }

  return false;
}

export default function CsvUploadConfigModal({
  data,
  proceed,
  isOpen,
  onClose,
  uploadAgain,
}: {
  data: CsvUpload;
  proceed: (data: Omit<ImportReq, "filepath">) => void;
  isOpen: boolean;
  onClose: (cancel: boolean) => void;
  uploadAgain: () => void;
}) {
  const [enableDataUpdate, setEnableDataUpdate] = useState(false);
  const [additionalOptions, setAdditionalOptions] =
    useState<AdditionalOptionsType>(ADD_OPTONS_INIT);

  useEffect(() => {
    if (!enableDataUpdate) {
      setAdditionalOptions(ADD_OPTONS_INIT);
    }
  }, [enableDataUpdate]);

  useEffect(() => {
    if (data.matches?.total_entries_to_be_updated) {
      setEnableDataUpdate(true);
    } else {
      setEnableDataUpdate(false);
    }
  }, [isOpen, data.matches]);

  const primaryButton1: ModalButtonContent = {
    label: "Import contacts",
    props: {
      onClick: () =>
        proceed({
          enable_update: enableDataUpdate,
          overwrite_values: additionalOptions.overwrite_values,
          ignore_blank: additionalOptions.ignore_blank,
        }),
      isLoading: isLoading(data.importing),
      isDisabled: isDisabled(data, enableDataUpdate),
    },
  };
  const primaryButton2 = {
    label: "Close",
    props: {
      onClick: () => onClose(false),
    },
  };

  const cancelButton = {
    label: "Cancel",
    props: {
      onClick: () => onClose(true),
    },
  };

  return (
    <IModal
      header={{ title: "Upload CSV file", capitalize: false }}
      primaryButton={
        isFinished(data.importing) ? primaryButton2 : primaryButton1
      }
      secondaryButton={isFinished(data.importing) ? undefined : cancelButton}
      isOpen={isOpen}
      onClose={() => onClose(false)}
      modalContentProps={{ bg: "grayV2.100", minW: "xl" }}
      size="2xl"
      modalBodyProps={{ h: "400px" }}
    >
      {!isFinished(data.importing) && data.matches && (
        <VStack>
          <BoxBorder>
            <BoxContainer>
              <ModalHeader status={statusFinder(data.matches)} />
              <Divider my="2" />
              <EnableDataUpdate
                value={enableDataUpdate}
                onChange={setEnableDataUpdate}
              />
              <Stats data={data.matches} showUpdateCount={enableDataUpdate} />

              <UploadStatusNote status={statusFinder(data.matches)} />
            </BoxContainer>

            <DownloadReport
              status={statusFinder(data.matches)}
              link={data.matches.report}
            />
          </BoxBorder>

          {!data.matches.error_contacts && (
            <Collapse in={enableDataUpdate} animateOpacity>
              <BoxBorder>
                <BoxContainer>
                  <AdditionalOptions
                    data={additionalOptions}
                    onChange={setAdditionalOptions}
                  />
                </BoxContainer>
              </BoxBorder>
            </Collapse>
          )}
        </VStack>
      )}

      {isFinished(data.importing) && (
        <UploadStats
          success={isSuccess(data.importing)}
          uploadAgain={uploadAgain}
          created={data.importedData?.new_records || 0}
          updated={data.importedData?.records_updated || 0}
        />
      )}
    </IModal>
  );
}
